<template>
  <section class="px-3 pb-3">
    <AdminToastStack :toasts="toasts" @close="removeToast" />

    <div class="admin-surface">
      <div class="admin-surface__head">
        <span class="admin-surface__title">Настройки стримов</span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadSettings">
            <i class="bi bi-arrow-repeat me-1"></i>
            Все настройки
          </button>
          <button type="button" class="btn btn-primary btn-sm" :disabled="isLoading" @click="resetForm">
            <i class="bi bi-plus-lg me-1"></i>
            Новый
          </button>
        </div>
      </div>

      <div class="row g-2 mt-1">
        <div class="col-12 col-lg-3">
          <input v-model.trim="searchQuery" type="search" class="form-control form-control-sm" placeholder="Локальный поиск" />
        </div>
        <div class="col-12 col-lg-3">
          <select v-model="byTypeFilter.streamType" class="form-select form-select-sm">
            <option value="">Тип стрима (для /by-type)</option>
            <option v-for="type in STREAM_TYPE_VALUES" :key="type" :value="type">{{ type }}</option>
          </select>
        </div>
        <div class="col-12 col-lg-3">
          <input
            v-model.trim="byTypeFilter.aiName"
            type="text"
            class="form-control form-control-sm"
            placeholder="Название AI (опционально)"
          />
        </div>
        <div class="col-12 col-lg-3 d-grid">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="loadSettingsByType">
            Загрузить по типу
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2 px-3 mt-3 mb-0">{{ successMessage }}</div>

      <div class="row g-3 mt-1">
        <div class="col-12 col-xxl-7">
          <div class="table-responsive admin-table-wrap">
            <table class="table table-sm align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th>Тип стрима</th>
                  <th>Название AI</th>
                  <th>Категория</th>
                  <th>Ключ настройки</th>
                  <th class="text-end">Действия</th>
                </tr>
              </thead>
              <tbody v-if="filteredSettings.length">
                <tr v-for="item in filteredSettings" :key="item.id" :class="{ 'admin-table__row--active': form.id === item.id }">
                  <td>{{ item.streamType }}</td>
                  <td>{{ item.aiName || '—' }}</td>
                  <td>{{ item.category || '—' }}</td>
                  <td>{{ item.settingKey || '—' }}</td>
                  <td class="text-end">
                    <div class="btn-group btn-group-sm">
                      <button class="btn btn-outline-primary" @click="editSetting(item)">Изменить</button>
                      <button class="btn btn-outline-danger" @click="deleteSetting(item)">Удалить</button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="5" class="text-center text-muted py-3">Настройки стримов не найдены.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-12 col-xxl-5">
          <div class="admin-detail">
            <div class="admin-detail__title">{{ form.id ? 'Редактирование настройки стрима' : 'Создание настройки стрима' }}</div>
            <small class="text-muted">Обязательные: Тип стрима, Название AI, Категория, Ключ настройки, Тип настройки</small>

            <form class="mt-2" @submit.prevent="submitForm">
              <div class="row g-2">
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">Тип стрима *</label>
                  <select v-model="form.streamType" class="form-select form-select-sm">
                    <option value="">Выберите тип</option>
                    <option v-for="type in STREAM_TYPE_VALUES" :key="type" :value="type">{{ type }}</option>
                  </select>
                </div>
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">Название AI *</label>
                  <input v-model.trim="form.aiName" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">Категория *</label>
                  <input v-model.trim="form.category" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">Ключ настройки *</label>
                  <input v-model.trim="form.settingKey" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Тип настройки *</label>
                  <input v-model.trim="form.settingType" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Описание</label>
                  <textarea v-model="form.description" rows="2" class="form-control form-control-sm"></textarea>
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Значение по умолчанию</label>
                  <input v-model="form.defaultValue" type="text" class="form-control form-control-sm" />
                </div>
              </div>

              <div class="mt-2">
                <AdminJsonTextarea
                  v-model="metadataText"
                  label="Метаданные JSON"
                  help-text="Оставьте пустым для null."
                  @validity-change="metadataValidation = $event"
                />
              </div>

              <div class="mt-2">
                <AdminJsonTextarea
                  v-model="dependenciesText"
                  label="Зависимости JSON"
                  help-text="Оставьте пустым для null."
                  @validity-change="dependenciesValidation = $event"
                />
              </div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetForm">Очистить</button>
                <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">
                  {{ form.id ? 'Сохранить' : 'Создать' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import AdminToastStack from '@/admin/components/AdminToastStack.vue'
import AdminJsonTextarea from '@/admin/components/AdminJsonTextarea.vue'
import { useAdminRequestState } from '@/admin/composables/useAdminRequestState'
import { STREAM_TYPE_VALUES, validateStreamSettingUpsertRequest } from '@/admin/types/adminContracts'
import {
  createAdminStreamSetting,
  deleteAdminStreamSetting,
  getAdminStreamSettings,
  getAdminStreamSettingsByType,
  updateAdminStreamSetting,
} from '@/admin/api/adminService'
import { normalizeSearchText, parseJsonInput, stringifyJsonInput } from '@/admin/utils/adminApiUtils'

const settings = ref([])
const searchQuery = ref('')

const byTypeFilter = reactive({
  streamType: '',
  aiName: '',
})

const form = reactive({
  id: null,
  streamType: '',
  aiName: '',
  category: '',
  settingKey: '',
  settingType: '',
  description: '',
  defaultValue: '',
})

const metadataText = ref('')
const dependenciesText = ref('')
const metadataValidation = ref({ isValid: true, error: '' })
const dependenciesValidation = ref({ isValid: true, error: '' })

const {
  isLoading,
  errorMessage,
  successMessage,
  toasts,
  removeToast,
  clearMessages,
  pushError,
  execute,
} = useAdminRequestState()

const filteredSettings = computed(() => {
  const query = normalizeSearchText(searchQuery.value)
  return settings.value.filter(item => {
    if (!query) return true
    const haystack = `${item.id} ${item.streamType} ${item.aiName} ${item.category} ${item.settingKey} ${item.settingType}`.toLowerCase()
    return haystack.includes(query)
  })
})

const setForm = item => {
  form.id = item?.id || null
  form.streamType = item?.streamType || ''
  form.aiName = item?.aiName || ''
  form.category = item?.category || ''
  form.settingKey = item?.settingKey || ''
  form.settingType = item?.settingType || ''
  form.description = item?.description || ''
  form.defaultValue = item?.defaultValue || ''
  metadataText.value = stringifyJsonInput(item?.metadata)
  dependenciesText.value = stringifyJsonInput(item?.dependencies)
}

const resetForm = () => {
  setForm(null)
}

const editSetting = item => {
  setForm(item)
}

const loadSettings = async () => {
  clearMessages()
  const list = await execute(() => getAdminStreamSettings(), {
    error: 'Не удалось получить настройки стримов.',
  })
  settings.value = list
}

const loadSettingsByType = async () => {
  if (!byTypeFilter.streamType) {
    pushError('Выберите тип стрима для запроса /by-type.')
    return
  }

  clearMessages()
  const list = await execute(
    () =>
      getAdminStreamSettingsByType({
        streamType: byTypeFilter.streamType,
        aiName: byTypeFilter.aiName,
      }),
    {
      error: 'Не удалось получить настройки стримов по типу.',
    }
  )
  settings.value = list
}

const submitForm = async () => {
  clearMessages()
  const validationErrors = validateStreamSettingUpsertRequest(form)
  if (validationErrors.length) {
    pushError(validationErrors[0])
    return
  }

  if (!metadataValidation.value.isValid) {
    pushError(metadataValidation.value.error || 'Невалидный JSON в метаданных.')
    return
  }
  if (!dependenciesValidation.value.isValid) {
    pushError(dependenciesValidation.value.error || 'Невалидный JSON в зависимостях.')
    return
  }

  const metadataResult = parseJsonInput(metadataText.value, { allowEmpty: true })
  if (!metadataResult.isValid) {
    pushError(metadataResult.error)
    return
  }

  const dependenciesResult = parseJsonInput(dependenciesText.value, { allowEmpty: true })
  if (!dependenciesResult.isValid) {
    pushError(dependenciesResult.error)
    return
  }

  const payload = {
    streamType: form.streamType,
    aiName: form.aiName,
    category: form.category,
    settingKey: form.settingKey,
    settingType: form.settingType,
    description: form.description,
    defaultValue: form.defaultValue,
    metadata: metadataResult.value,
    dependencies: dependenciesResult.value,
  }

  if (form.id) {
    await execute(() => updateAdminStreamSetting(form.id, payload), {
      success: 'Настройка стрима обновлена.',
      error: 'Не удалось обновить настройку стрима.',
    })
  } else {
    await execute(() => createAdminStreamSetting(payload), {
      success: 'Настройка стрима создана.',
      error: 'Не удалось создать настройку стрима.',
    })
  }

  resetForm()
  await loadSettings()
}

const deleteSetting = async item => {
  const confirmed = window.confirm('Удалить настройку стрима?')
  if (!confirmed) return

  clearMessages()
  await execute(() => deleteAdminStreamSetting(item.id), {
    success: 'Настройка стрима удалена.',
    error: 'Не удалось удалить настройку стрима.',
  })

  if (form.id === item.id) resetForm()
  await loadSettings()
}

onMounted(async () => {
  await loadSettings()
})
</script>

<style scoped>
.admin-surface {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.admin-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-surface__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
}

.admin-table-wrap {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  max-height: 72vh;
  overflow: auto;
}

.admin-table thead th {
  position: sticky;
  top: 0;
  background: #f8fbff;
  z-index: 2;
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9edf6;
}

.admin-table__row--active td {
  background: #eef5ff;
}

.admin-detail {
  border: 1px solid #e4ebf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px;
}

.admin-detail__title {
  color: #1f2d45;
  font-weight: 700;
}
</style>
